import { useEffect, useRef } from "react";
import { LIMITS } from "@shared/contracts";
import { GlowPanel } from "../../platform/ui/GlowPanel";
import { FormField } from "./FormField";
import { TurnstileWidget } from "./TurnstileWidget";
import { useContactForm } from "./useContactForm";

type Props = { turnstileSiteKey: string; listingSlug?: string; initialMessage?: string };

export function ContactForm({ turnstileSiteKey, listingSlug, initialMessage }: Props) {
  const form = useContactForm(listingSlug);
  const summary = useRef<HTMLParagraphElement>(null);
  const sent = useRef<HTMLDivElement>(null);
  const formError = form.state.status === "editing" ? form.state.formError : null;

  useEffect(() => {
    if (formError) summary.current?.focus();
  }, [formError]);

  useEffect(() => {
    if (form.state.status === "sent") sent.current?.focus();
  }, [form.state.status]);

  if (form.state.status === "sent") {
    return (
      <GlowPanel variant="feature">
        <div className="contact-sent" ref={sent} tabIndex={-1} role="status">
          <h2 className="contact-sent__title">She has it</h2>
          <p>
            Your message is waiting for Orla and nobody else can read it. If she replies, it will be
            to the address you gave.
          </p>
        </div>
      </GlowPanel>
    );
  }

  const errors = form.state.status === "editing" ? form.state.fieldErrors : {};
  const sending = form.state.status === "sending";

  return (
    <GlowPanel variant="feature">
      <form className="contact-form" onSubmit={form.submit} noValidate aria-busy={sending}>
        {formError ? (
          <p className="contact-form__error" ref={summary} tabIndex={-1} role="alert">
            {formError}
          </p>
        ) : null}
        <FormField label="Your name" error={errors.name} className="contact-form__name">
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              name="name"
              type="text"
              autoComplete="name"
              maxLength={LIMITS.contactName.max}
              required
              aria-invalid={invalid}
              aria-describedby={describedBy}
            />
          )}
        </FormField>
        <FormField
          label="Your email"
          hint="Orla replies to this address."
          error={errors.email}
          className="contact-form__email"
        >
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              name="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              maxLength={254}
              required
              aria-invalid={invalid}
              aria-describedby={describedBy}
            />
          )}
        </FormField>
        <FormField label="Message" error={errors.message} className="contact-form__message">
          {({ id, describedBy, invalid }) => (
            <textarea
              id={id}
              name="message"
              defaultValue={initialMessage}
              maxLength={LIMITS.contactMessage.max}
              required
              aria-invalid={invalid}
              aria-describedby={describedBy}
            />
          )}
        </FormField>
        <TurnstileWidget
          siteKey={turnstileSiteKey}
          resetToken={form.challengeRound}
          onToken={form.setChallengeToken}
        />
        <button
          type="submit"
          className="contact-form__send glass"
          data-glass-interactive
          disabled={sending}
        >
          {sending ? "Sending" : "Send to Orla"}
        </button>
      </form>
    </GlowPanel>
  );
}
